import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaFacebookF, FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import Header from '../components/Header/Header';
import servicesDetailData from '../data/servicesDetailData';
import './BlogDetail.css';

const allPosts = [
  {
    id: 1,
    title: "5 Signs Your Home Needs Electrical Rewiring",
    date: "April 15, 2026",
    author: "Musa Electrical Team",
    image: "/images/blog/blog-1.jpg",
    excerpt: "Is your home showing signs of electrical problems? Here are 5 warning signs that indicate you might need professional rewiring services soon.",
    category: "Electrical Tips",
    content: `Your home's electrical system is the backbone of your daily comfort and safety. Over time, wiring can deteriorate, leading to potential hazards that put your family at risk.

### 1. Frequent Circuit Breaker Trips
If your circuit breaker trips regularly, it's a clear sign your electrical system is overloaded. Modern homes demand more power than older systems were designed to handle. Upgrading your panel may be the solution.

### 2. Flickering or Dimming Lights
When lights flicker or dim unexpectedly, especially when using major appliances, it indicates voltage fluctuation. This could stem from loose wiring, overloaded circuits, or a failing electrical panel.

### 3. Burning Smell or Scorch Marks
A burning odor near outlets or switches is a serious warning sign. Scorch marks on outlets suggest arcing and overheating, which can lead to electrical fires if not addressed immediately.

### 4. Outdated Wiring Systems
Homes built before 1980 may have aluminum wiring or knob-and-tube systems. These outdated setups don't meet modern safety standards and should be replaced by certified electricians.

### 5. Buzzing Sounds from Outlets
Any audible buzzing or sizzling from outlets, switches, or the breaker panel indicates loose connections or faulty components that need professional attention.

### Conclusion
Don't ignore these warning signs. Contact Musa Electrical for a comprehensive inspection and professional rewiring services to keep your home safe and up to code.`
  },
  {
    id: 2,
    title: "Benefits of Installing Solar Panels in Kenya",
    date: "April 10, 2026",
    author: "Musa Electrical Team",
    image: "/images/blog/blog-2.jpg",
    category: "Solar Energy",
    content: `Kenya receives abundant sunshine year-round, making it one of the best locations for solar energy adoption. Here's why you should consider solar panels for your home or business.

### Significant Cost Savings
Solar panels can reduce your electricity bills by up to 70%. With Kenya Power's rising tariffs, generating your own electricity provides long-term financial relief.

### Reliable Power Supply
Frequent power outages affect productivity and comfort. Solar systems with battery storage provide uninterrupted power, keeping your lights on and appliances running even during blackouts.

### Environmental Benefits
Solar energy is clean and renewable. By switching to solar, you reduce your carbon footprint and contribute to Kenya's sustainability goals.

### Low Maintenance
Once installed, solar systems require minimal maintenance. Annual cleaning and occasional inspections keep them running efficiently for 25+ years.

Ready to go solar? Contact Musa Electrical for a free consultation and quote.`
  },
  {
    id: 3,
    title: "Choosing the Right CCTV System",
    date: "April 5, 2026",
    author: "Musa Electrical Team",
    image: "/images/blog/blog-3.jpg",
    category: "Security",
    content: `Selecting the right CCTV system can be overwhelming with so many options available. Here's a guide to help you make an informed decision.

### Camera Types
Bullet Cameras are ideal for outdoor use and serve as a visible deterrent. Dome Cameras are discreet and vandal-resistant, suitable for indoor and outdoor use. PTZ Cameras offer pan, tilt, and zoom capabilities for wide coverage. IP Cameras provide high definition with network connectivity.

### Resolution
For clear identification, 1080p is the minimum recommended resolution. 4K cameras provide superior detail but require more storage and bandwidth.

### Storage Options
DVR systems store footage locally on hard drives. NVR systems offer cloud backup and remote access. Consider your retention needs when choosing storage capacity.

### Professional Installation
Proper camera placement is crucial for coverage. Musa Electrical provides expert installation and configuration for optimal security.`
  }
];

const recentPosts = [
  { id: 4, title: "Electrical Safety Tips Every Homeowner Should Know", date: "March 28, 2026", image: "/images/blog/blog-4.jpg" },
  { id: 5, title: "The Future of Smart Home Technology", date: "March 20, 2026", image: "/images/blog/blog-5.jpg" },
  { id: 6, title: "Cost Guide: Electrical Installation in New Buildings", date: "March 15, 2026", image: "/images/blog/blog-6.jpg" }
];

const initialComments = [
  { id: 1, name: "John M.", date: "April 16, 2026", text: "Great article! I just had my wiring checked after noticing some of these signs. Thanks for the helpful information." },
  { id: 2, name: "Sarah K.", date: "April 17, 2026", text: "Very informative. I'm planning to upgrade my electrical system and this really helped me understand what to look for." }
];

const BlogDetail = () => {
  const { id } = useParams();
  const post = allPosts.find(p => p.id === parseInt(id));
  const [comments, setComments] = useState(initialComments);
  const [newComment, setNewComment] = useState({ name: '', email: '', text: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (!newComment.name || !newComment.text) return;
    setComments([...comments, { id: comments.length + 1, name: newComment.name, date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }), text: newComment.text }]);
    setNewComment({ name: '', email: '', text: '' });
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  if (!post) {
    return (
      <div className="page-wrapper">
        <Header />
        <div className="not-found"><h1>Article Not Found</h1><Link to="/blog">Back to Blog</Link></div>
      </div>
    );
  }

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="page-wrapper">
      <div className="hero-wrapper">
        <Header />
        <div className="page-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + post.image})` }}>
          <div className="page-hero-overlay" />
          <div className="page-hero-content">
            <h1>{post.title}</h1>
            <div className="breadcrumb">
              <a href="/">Home</a><span className="sep">/</span>
              <a href="/blog">Blog</a><span className="sep">/</span>
              <span className="active">{post.title}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="blogdetail-container">
        <div className="blogdetail-row">
          <div className="blogdetail-main">
            {/* Featured Image with Category Tag on Top Left */}
            <div className="featured-image-wrap">
              <span className="featured-category-tag">{post.category}</span>
              <img src={process.env.PUBLIC_URL + post.image} alt={post.title} className="featured-img" />
            </div>

            <div className="article-meta">
              <span>{post.date}</span>
              <span>By {post.author}</span>
            </div>
            
            <div className="article-content">
              {post.content.split('\n\n').map((paragraph, i) => {
                if (paragraph.startsWith('### ')) {
                  return <h3 key={i} className="article-heading">{paragraph.replace('### ', '')}</h3>;
                }
                return <p key={i}>{paragraph}</p>;
              })}
            </div>

            <div className="article-share">
              <span>Share:</span>
              <a href={`https://facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="share-btn fb"><FaFacebookF /> Facebook</a>
              <a href={`https://x.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer" className="share-btn x"><FaXTwitter /> X</a>
              <a href={`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="share-btn wa"><FaWhatsapp /> WhatsApp</a>
            </div>

            {/* Comments */}
            <div className="comments-section">
              <h3>Comments ({comments.length})</h3>
              {comments.map((comment) => (
                <div className="comment" key={comment.id}>
                  <div className="comment-avatar">{comment.name.charAt(0)}</div>
                  <div className="comment-body">
                    <div className="comment-header"><strong>{comment.name}</strong><span>{comment.date}</span></div>
                    <p>{comment.text}</p>
                  </div>
                </div>
              ))}
              <div className="comment-form-wrapper">
                <h4>Leave a Comment</h4>
                {submitted && <p className="comment-success">Your comment has been posted!</p>}
                <form className="comment-form" onSubmit={handleCommentSubmit}>
                  <div className="comment-form-row">
                    <input type="text" placeholder="Your Name *" value={newComment.name} onChange={(e) => setNewComment({...newComment, name: e.target.value})} required />
                    <input type="email" placeholder="Email (optional)" value={newComment.email} onChange={(e) => setNewComment({...newComment, email: e.target.value})} />
                  </div>
                  <textarea placeholder="Write your comment..." value={newComment.text} onChange={(e) => setNewComment({...newComment, text: e.target.value})} required rows="3"></textarea>
                  <button type="submit" className="comment-submit">Post Comment</button>
                </form>
              </div>
            </div>
          </div>

          <div className="blogdetail-sidebar">
            <div className="sidebar-card">
              <h4>Related Articles</h4>
              {recentPosts.map((rp) => (
                <Link to={`/blog/${rp.id}`} className="related-item" key={rp.id}>
                  <img src={process.env.PUBLIC_URL + rp.image} alt={rp.title} />
                  <div><h5>{rp.title}</h5><span>{rp.date}</span></div>
                </Link>
              ))}
            </div>
            <div className="sidebar-card">
              <h4>Our Services</h4>
              <ul>{servicesDetailData.slice(0, 6).map(s => (<li key={s.id}><Link to={`/services/${s.slug}`}>{s.name}</Link></li>))}</ul>
            </div>
            <div className="sidebar-cta">
              <h4>Need Professional Help?</h4>
              <p>Contact us today for expert electrical services</p>
              <a href="tel:0796049254" className="sidebar-cta-btn">📞 Call Now</a>
              <a href="/contact" className="sidebar-cta-btn secondary">Get a Quote</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
